import { useMemo } from 'react';
import { useLocation } from 'react-router';

/**
 * Creates a custom hook that safely extracts location state with default values
 * and memoizes the result to prevent unnecessary re-renders.
 *
 * @param defaultState - Object containing default values for all state properties
 * @returns A hook that returns memoized location state
 */
export function createUseSafeLocationState<T extends Record<string, any>>(defaultState: T) {
  return (): T => {
    const { state } = useLocation() as Partial<{ state: Partial<T> }>;

    // Extract all values into an array for dependencies
    // This creates a flat array of primitive values that ESLint can work with
    const values = Object.keys(defaultState).map((key) => state?.[key] ?? defaultState[key]);

    // Reconstruct the object inside useMemo using the dependency values
    return useMemo(() => {
      const result: Record<string, unknown> = {};
      Object.keys(defaultState).forEach((key, index) => {
        result[key] = values[index];
      });
      return result as T;
      // We need to disable exhaustive-deps because we're dynamically building
      // the dependency array based on the generic type T. The deps array contains
      // all individual primitive state values that should trigger re-memoization.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, values);
  };
}
